import {
  type UserGroupListItem,
  UserGroupTypes,
} from "@/models/user-group.model";
import { IconButton } from "@spz/ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@spz/ui/components/tooltip";
import type { Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useCreateOrUpdateUserGroupsDialog } from "../../dialogs/use-create-or-update-user-groups-dialog";
import { useDeleteUserGroupsDialog } from "../../dialogs/use-delete-user-groups-dialog";
import { selectAccessControlViewModel } from "../../view-models/select-access-control-view-model";

interface UserGroupsActionsCellProps {
  row: Row<UserGroupListItem>;
}

export function UserGroupsActionsCell({ row }: UserGroupsActionsCellProps) {
  const { t } = useTranslation();
  const userGroup = row.original;
  const deleteUserGroupsPopover = useDeleteUserGroupsDialog();
  const editUserGroupDialog = useCreateOrUpdateUserGroupsDialog();
  const { openAccessControlSheet } = selectAccessControlViewModel({});

  const handleAccessControls = () => {
    openAccessControlSheet({
      subjectId: userGroup.id.toString(),
      subjectType: UserGroupTypes.USER_GROUP,
      subjectName: userGroup.name,
    });
  };

  const handleEdit = () => {
    editUserGroupDialog.open({
      groupId: userGroup.id,
    });
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    deleteUserGroupsPopover.open({
      groupId: userGroup.id,
      anchorElement: e.currentTarget,
      updatedAt: userGroup.updatedAt || undefined,
    });
  };

  return (
    <div className="flex items-center gap-1 justify-end">
      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            icon="key"
            variant="ghost"
            onClick={handleAccessControls}
            ghostInTableCell
          />
        </TooltipTrigger>
        <TooltipContent>{t("access_controls")}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            icon="edit"
            variant="ghost"
            onClick={handleEdit}
            ghostInTableCell
          />
        </TooltipTrigger>
        <TooltipContent>{t("edit_group")}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            icon="trash-2"
            variant="ghost"
            onClick={handleDelete}
            ghostInTableCell
          />
        </TooltipTrigger>
        <TooltipContent>{t("delete_group")}</TooltipContent>
      </Tooltip>
    </div>
  );
}
